
import React from 'react';
import { Link } from 'react-router-dom';
import HeaderNavigation from './header/HeaderNavigation';
import HeaderMobileMenu from './header/HeaderMobileMenu';
import UserNavigation from './header/UserNavigation';
import { AuthButtons } from './header/AuthButtons';
import { useHeaderAuth } from '@/hooks/useHeaderAuth';
import { useTaskNotifications } from '@/hooks/useTaskNotifications';
import TaskNotification from '@/components/profile/TaskNotification';
import { useAuth, isSuperAdmin } from '@/context/SupabaseAuthContext';
import { addIconsToTasks } from '@/utils/taskIcons';
import HeaderBranding from './header/HeaderBranding';

interface HeaderProps {
  isLoggedIn?: boolean;
  isAdmin?: boolean;
  userRole?: string;
  brandName?: string;
  onLogout?: () => void;
}

const Header: React.FC<HeaderProps> = ({ 
  isLoggedIn = false,
  isAdmin,
  userRole,
  brandName,
  onLogout
}) => {
  const headerAuth = useHeaderAuth();
  const { user, profile } = useAuth();
  const { tasks, markTaskComplete } = useTaskNotifications(user as any);
  
  const handleLogout = onLogout || headerAuth.handleLogout;

  // Transform tasks to include icons
  const tasksWithIcons = addIconsToTasks(tasks);

  const getDashboardLink = () => {
    if (!userRole) return '/dashboard';
    
    switch(userRole.toLowerCase()) {
      case 'admin':
        return '/admin/dashboard';
      case 'superadmin':
        return '/super-admin/dashboard';
      case 'masteradmin':
        return '/master/dashboard';
      case 'business':
      case 'manager':
        return '/business/dashboard';
      default:
        return '/user/dashboard';
    }
  };

  return (
    <header className="bg-primary-blue shadow-md text-white sticky top-0 z-40">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-24 md:h-32 gap-4">
          <HeaderBranding
            isMasterAdmin={userRole?.toLowerCase() === 'masteradmin'}
            isMasterLicenseRoute={false}
            brandName={brandName}
          />

          <HeaderNavigation />

          <div className="flex items-center gap-2">
            {isLoggedIn && (
              <TaskNotification
                tasks={tasksWithIcons}
                onTaskComplete={markTaskComplete}
              />
            )}

            {isLoggedIn && (
              <div className="hidden md:flex">
                <UserNavigation
                  isUserDashboard={location.pathname.startsWith('/user/')}
                  getDashboardLink={getDashboardLink}
                />
              </div>
            )}

            <div className="hidden md:flex">
              <AuthButtons isLoggedIn={isLoggedIn} />
            </div>

            <HeaderMobileMenu
              isLoggedIn={isLoggedIn}
              onLogout={handleLogout}
              isUserDashboard={location.pathname.startsWith('/user/')}
              getDashboardLink={getDashboardLink}
            />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

